import React, { useEffect, useState } from "react";
import { listAll, createDoc, updateDocById, removeDoc } from "../../services/firestore";
import { Plus, Save, Trash2, Truck, Zap, Store, Mail, Package, Pencil, X, Check } from "lucide-react";

const ICONS = [
  { v: "truck", label: "Truck", I: Truck },
  { v: "zap", label: "Zap (Express)", I: Zap },
  { v: "store", label: "Store (Pickup)", I: Store },
  { v: "mail", label: "Mail (Post)", I: Mail },
  { v: "package", label: "Package", I: Package },
];

function emptyForm() {
  return {
    code: "",
    name: "",
    description: "",
    duration: "",
    price_azn: 0,
    icon: "truck",
    active: true,
  };
}

export default function AdminDelivery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const rows = await listAll("delivery_methods");
      // sort: active first, then by price asc
      rows.sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return (a.price_azn || 0) - (b.price_azn || 0);
      });
      setItems(rows);
    } catch (e) {
      setMsg(e.message || "Yüklənmə xətası.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const flash = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(""), 2500);
  };

  const startEdit = (item) => {
    setForm({
      code: item.code || "",
      name: item.name || "",
      description: item.description || "",
      duration: item.duration || "",
      price_azn: Number(item.price_azn || 0),
      icon: item.icon || "truck",
      active: item.active !== false,
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const startNew = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
  };

  const cancel = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      flash("Kod və ad mütləqdir.");
      return;
    }
    setBusy(true);
    try {
      const code = form.code.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "-");
      const payload = {
        code,
        name: form.name.trim(),
        description: form.description.trim(),
        duration: form.duration.trim(),
        price_azn: Number(form.price_azn) || 0,
        icon: form.icon,
        active: !!form.active,
      };
      if (editingId) {
        await updateDocById("delivery_methods", editingId, payload);
        flash("Yadda saxlandı. ✓");
      } else {
        await createDoc("delivery_methods", payload, `del-${code}-${Date.now().toString(36).slice(-4)}`);
        flash("Əlavə edildi. ✓");
      }
      cancel();
      load();
    } catch (e) {
      flash(e.message || "Xəta.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await updateDocById("delivery_methods", item.id, { active: !item.active });
      load();
    } catch (e) {
      flash(e.message || "Xəta.");
    }
  };

  const del = async (item) => {
    if (!window.confirm(`"${item.name}" çatdırılma növünü silmək istəyirsiniz?`)) return;
    try {
      await removeDoc("delivery_methods", item.id);
      flash("Silindi. ✓");
      load();
    } catch (e) {
      flash(e.message || "Xəta.");
    }
  };

  const IconFor = ({ icon }) => {
    const found = ICONS.find((x) => x.v === icon) || ICONS[0];
    const I = found.I;
    return <I size={18} />;
  };

  return (
    <div className="p-10" data-testid="admin-delivery">
      <div className="flex items-end justify-between mb-8 sticky top-0 bg-[var(--bg)] z-10 pb-4 border-b border-[var(--border-soft)]">
        <div>
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Satış</div>
          <h1 className="font-display text-5xl mt-1">Çatdırılma Növləri</h1>
          <p className="text-sm text-[var(--text-2)] mt-2 max-w-2xl">
            Sifariş zamanı müştərinin seçə biləcəyi çatdırılma variantlarını buradan idarə edin.
            Qeyri-aktiv olanlar checkout səhifəsində görsənmir.
          </p>
        </div>
        {!showForm && (
          <button onClick={startNew} className="btn-primary" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="add-delivery">
            <Plus size={14}/> Yeni növ
          </button>
        )}
      </div>

      {msg && <div className="mb-4 text-sm text-[var(--primary)]">{msg}</div>}

      {showForm && (
        <div className="mb-8 border border-[var(--border)] p-5 bg-[var(--surface)]/40" data-testid="delivery-form">
          <div className="flex items-center justify-between mb-5">
            <div className="font-display text-xl">{editingId ? "Redaktə et" : "Yeni çatdırılma növü"}</div>
            <button onClick={cancel} className="btn-ghost" aria-label="Bağla"><X size={14}/></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Kod *</label>
              <input
                className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm"
                placeholder="standard, express, pickup..."
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={!!editingId}
                data-testid="del-form-code"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">Yalnız hərflər, rəqəmlər və tire (-). Yaradıldıqdan sonra dəyişməz.</p>
            </div>
            <div>
              <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Ad *</label>
              <input
                className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                placeholder="Standart çatdırılma"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="del-form-name"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Təsvir</label>
              <input
                className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                placeholder="məs: 3-5 günə qapınıza"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="del-form-desc"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Müddət</label>
              <input
                className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                placeholder="3-5 GÜN, 24 SAAT, ANI..."
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                data-testid="del-form-duration"
              />
            </div>
            <div>
              <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Qiymət (AZN)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                placeholder="0"
                value={form.price_azn}
                onChange={(e) => setForm({ ...form, price_azn: e.target.value })}
                data-testid="del-form-price"
              />
              <p className="text-[11px] text-[var(--text-muted)] mt-1">0 yazılarsa "Pulsuz" kimi göstərilir.</p>
            </div>
            <div>
              <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">İkon</label>
              <select
                className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                data-testid="del-form-icon"
              >
                {ICONS.map((i) => <option key={i.v} value={i.v}>{i.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2 pt-2">
              <input
                id="del-active"
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                data-testid="del-form-active"
              />
              <label htmlFor="del-active" className="text-sm">Aktiv (checkout-da göstər)</label>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <button onClick={submit} disabled={busy} className="btn-primary" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="del-form-save">
              <Save size={14}/> {busy ? "…" : (editingId ? "Yadda saxla" : "Əlavə et")}
            </button>
            <button onClick={cancel} className="btn-outline" style={{ textTransform: "none", letterSpacing: "0" }}>Ləğv et</button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-[var(--text-muted)]">Yüklənir…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] py-14 text-center text-[var(--text-muted)] text-sm">
          Hələ çatdırılma növü əlavə edilməyib. <button onClick={startNew} className="underline">İlk növü əlavə et</button>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((it) => (
            <div key={it.id} className="border border-[var(--border)] p-4 flex items-center gap-4" data-testid={`delivery-row-${it.code}`}>
              <div className="w-10 h-10 border border-[var(--border)] flex items-center justify-center flex-shrink-0">
                <IconFor icon={it.icon}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-lg truncate">{it.name}</span>
                  <span className="font-mono text-[11px] text-[var(--text-muted)] px-2 py-0.5 border border-[var(--border)]">{it.code}</span>
                  {!it.active && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[var(--text-muted)]/15 text-[var(--text-muted)]">Qeyri-aktiv</span>
                  )}
                </div>
                <div className="text-sm text-[var(--text-2)] mt-1 truncate">
                  {it.description || <span className="text-[var(--text-muted)]">(təsvir yoxdur)</span>}
                </div>
                <div className="text-[11px] text-[var(--text-muted)] mt-1 flex gap-4 flex-wrap">
                  <span>Müddət: <span className="text-[var(--text-2)]">{it.duration || "—"}</span></span>
                  <span>Qiymət: <span className="text-[var(--text-2)]">{(it.price_azn || 0) === 0 ? "Pulsuz" : `${Number(it.price_azn).toFixed(2)} ₼`}</span></span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => toggleActive(it)}
                  className="btn-ghost"
                  title={it.active ? "Qeyri-aktiv et" : "Aktiv et"}
                  data-testid={`delivery-toggle-${it.code}`}
                >
                  {it.active ? <Check size={14}/> : <X size={14}/>}
                </button>
                <button onClick={() => startEdit(it)} className="btn-ghost" title="Redaktə" data-testid={`delivery-edit-${it.code}`}>
                  <Pencil size={14}/>
                </button>
                <button onClick={() => del(it)} className="btn-ghost text-[var(--accent)] hover:!text-white hover:!bg-[var(--accent)] hover:!border-[var(--accent)]" title="Sil" data-testid={`delivery-del-${it.code}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
